import { Locale, Translation } from 'locale'
import { RxWebSocketReadyState, shouldInvalidateCredentials, socketCloseReason } from 'messaging'
import { ServerActionTypes, EntityUpdateAction, EntityType, ClientLoginAction, ClientActionTypes } from 'api'
import { ConnectionStore, CredentialsStatus, ConnectionStatus } from './connectionTypes'
import { ConnectionActionTypes, ConnectionActions } from './connectionActions'
import { diagnosticsLogger } from '../../logger/diagnosticsLogger'

export const initialState: ConnectionStore = {
  credentialsStatus: CredentialsStatus.NotSet,
  connectionStatus: ConnectionStatus.Disconnected,
  socketStatus: [],
  connectedOnce: false,
  loggedInOnce: false,
  lastUsername: null,
  lastErrorMessage: null,
  lastSocketErrorMessage: null,
  loginCorrelationId: null,
  authorisedUser: null
}

type ConnectionReducerAction = ConnectionActions | EntityUpdateAction | ClientLoginAction

export function connectionReducer(state: ConnectionStore = initialState, action: ConnectionReducerAction): ConnectionStore {
  switch (action.type) {
    case ConnectionActionTypes.SOCKET_STATUS: {
      const status = action.payload
      diagnosticsLogger.log(`Connection reducer - status.readyState changed. Status: ${JSON.stringify(status)}`)
      if (status.readyState === RxWebSocketReadyState.Open) {
        return {
          ...state,
          connectionStatus: ConnectionStatus.Connected,
          connectedOnce: true,
          socketStatus: [],
          lastSocketErrorMessage: null
        }
      } else if (status.readyState === RxWebSocketReadyState.Closed) {
        return {
          ...state,
          connectionStatus: ConnectionStatus.Disconnected,
          credentialsStatus: shouldInvalidateCredentials(status.closeCode) ? CredentialsStatus.NotSet : state.credentialsStatus,
          socketStatus: [...state.socketStatus, status],
          lastSocketErrorMessage: socketCloseReason(status.closeCode) || status.closeReason
        }
      } else if (status.readyState === RxWebSocketReadyState.Errored) {
        return {
          ...state,
          connectionStatus: ConnectionStatus.Disconnected,
          socketStatus: [...state.socketStatus, status],
          lastSocketErrorMessage:
            (status.error && status.error.message) || Locale.translate(Translation.SC_FAILED_TO_CONNECT_TO, { endpoint: status.endpoint })
        }
      } else {
        return {
          ...state,
          socketStatus: [...state.socketStatus, status]
        }
      }
    }

    case ConnectionActionTypes.SET_CREDENTIALS: {
      const credentialsStatus = CredentialsStatus.Available
      const lastUsername = action.payload.username
      const lastErrorMessage = null
      const lastSocketErrorMessage = null
      return { ...state, credentialsStatus, lastUsername, lastErrorMessage, lastSocketErrorMessage }
    }

    case ConnectionActionTypes.CLEAR_LAST_ERRORS: {
      const lastErrorMessage = null
      const lastSocketErrorMessage = null
      return { ...state, lastErrorMessage, lastSocketErrorMessage }
    }

    case ConnectionActionTypes.SIGN_OUT: {
      const credentialsStatus = CredentialsStatus.NotSet
      const loginCorrelationId = null
      const lastErrorMessage = action.payload
      return { ...state, credentialsStatus, loginCorrelationId, lastErrorMessage }
    }

    case ServerActionTypes.ENTITY_UPDATE: {
      if (action.payload.update.entityType === EntityType.AUTHORISED_USER) {
        return {
          ...state,
          authorisedUser: action.payload.update.entity,
          loginCorrelationId: null,
          connectionStatus: ConnectionStatus.LoggedIn
        }
      } else {
        return state
      }
    }

    case ClientActionTypes.LOGIN_MESSAGE: {
      return {
        ...state,
        loginCorrelationId: action.payload.correlationId
      }
    }

    case ConnectionActionTypes.FORCE_DISCONNECT: {
      return {
        ...state,
        lastErrorMessage: 'disconnection forced',
        connectionStatus: ConnectionStatus.Disconnected
      }
    }

    default: {
      return state
    }
  }
}
